import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { styled } from "@linaria/react";
import { ArrowCounterClockwiseIcon, CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

import RotateWithTransition from "../animations/RotateWithTransition";
import FlexItem from "../containers/FlexItem";
import FlexWrapper, { AlignItems, FlexDirection } from "../containers/FlexWrapper";
import Spacing from "../containers/Spacing";
import HorizontalDivider from "../dividers/HorizontalDivider";
import Dropdown, { DropdownVariant } from "../dropdown/Dropdown";
import Icon, { IconVariant, IconWeight } from "../icons/Icon";
import Text, { TextSize, TextVariant } from "../text/Text";
import { useControlledOpenState, useDebouncedValue, useElementWidth } from "./hooks";
import { InputLabel } from "./Input";

export enum SelectInputVariant {
  BASE = "BASE",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
}

export enum SelectInputSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export interface SelectInputOption {
  id: string;
  label: string;
  value: unknown;
  icon?: React.ReactNode;
  variant?: TextVariant;
}

export interface PinnedOption {
  id: string;
  label: string;
  optionId: string;
}

export interface SelectInputProps {
  label?: string;
  variant?: SelectInputVariant;
  size?: SelectInputSize;
  options: SelectInputOption[];
  value?: SelectInputOption | null;
  placeholder?: string;
  onChange?: (value: SelectInputOption) => void;
  width?: number | string;
  fillWidth?: boolean;
  dropdownWidth?: number | string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onReset?: () => void;
  pinnedOptions?: PinnedOption[];
  error?: string;
  onSearch?: (searchTerm: string, options: SelectInputOption[]) => SelectInputOption[];
  debounceMs?: number;
}

const StyledSelectInput = withTheme(styled.div<
  PropsWithTheme<{
    $variant: SelectInputVariant;
    $size: SelectInputSize;
    $isActive: boolean;
    $isDisabled: boolean;
    $isError: boolean;
    $width?: number | string;
    $fillWidth?: boolean;
  }>
>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => "0 6px")
      .with(SelectInputSize.MEDIUM, () => "0 8px")
      .with(SelectInputSize.LARGE, () => "0 10px")
      .exhaustive();
  }};

  height: ${({ $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => "24px")
      .with(SelectInputSize.MEDIUM, () => "28px")
      .with(SelectInputSize.LARGE, () => "32px")
      .exhaustive();
  }};
  width: ${({ $fillWidth, $width }) =>
    $fillWidth ? "100%" : $width ? getCssSizeValue($width) : "auto"};

  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};

  background: ${({ theme }) => theme.color.background.primary};
  border: 0.5px solid
    ${({ theme, $isActive, $isError }) => {
      if ($isActive) {
        return theme.color.border.selected;
      }

      if ($isError) {
        return theme.color.border.error;
      }

      return theme.color.border.primary;
    }};
  border-radius: 4px;

  transition: border-color 0.075s ease-in-out;

  &:hover {
    border: 0.5px solid
      ${({ theme, $isActive }) =>
        $isActive ? theme.color.border.selected : theme.color.border.primary};
  }
`);

const StyledSearchInput = withTheme(styled.input<
  PropsWithTheme<{
    $variant: SelectInputVariant;
    $size: SelectInputSize;
    $isActive: boolean;
    $isDisabled: boolean;
    $isError: boolean;
    $width?: number | string;
    $fillWidth?: boolean;
  }>
>`
  flex: 1;
  outline: none;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;

  height: ${({ $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => "24px")
      .with(SelectInputSize.MEDIUM, () => "28px")
      .with(SelectInputSize.LARGE, () => "32px")
      .exhaustive();
  }};

  color: ${({ theme }) => theme.color.text.primary};

  font-size: ${({ theme, $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => theme.font.sans.size.body_sm)
      .with(SelectInputSize.MEDIUM, () => theme.font.sans.size.body_md)
      .with(SelectInputSize.LARGE, () => theme.font.sans.size.body_lg)
      .exhaustive();
  }};
  line-height: ${({ theme, $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => theme.font.sans.height.body_sm)
      .with(SelectInputSize.MEDIUM, () => theme.font.sans.height.body_md)
      .with(SelectInputSize.LARGE, () => theme.font.sans.height.body_lg)
      .exhaustive();
  }};
  font-weight: ${({ theme }) => theme.font.sans.weight.medium};
  font-family: ${({ theme }) => theme.font.sans.family};

  &::placeholder {
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  &:disabled {
    cursor: not-allowed;
  }
`);

const SelectInputContent = styled.div<{ $fillWidth?: boolean; $width?: number | string }>`
  display: flex;
  flex-direction: column;

  min-width: ${({ $width }) => getCssSizeValue($width, 200)};
  max-height: 300px;
  width: ${({ $fillWidth, $width }) => {
    if ($width) {
      return getCssSizeValue($width);
    }
    return $fillWidth ? "100%" : "auto";
  }};
`;

const SelectInputOptionsContainer = styled.div<{ $fillWidth?: boolean; $width?: number | string }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  width: ${({ $fillWidth, $width }) => ($fillWidth || $width ? "100%" : "auto")};
`;

const SelectInputOption = withTheme(styled.button<
  PropsWithTheme<{
    $isSelected: boolean;
    $isFocused?: boolean;
  }>
>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px;

  background: ${({ theme, $isSelected, $isFocused }) => {
    if ($isSelected || $isFocused) {
      return theme.color.background.tertiary;
    }
    return "transparent";
  }};
  border: none;
  border-radius: 4px;

  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.color.background.tertiary};
  }

  &:focus {
    outline: none;
    background-color: ${({ theme }) => theme.color.background.tertiary};
  }
`);

const PinnedOptionButton = withTheme(styled.button<
  PropsWithTheme<{
    $isSelected?: boolean;
    $isFocused?: boolean;
  }>
>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px;

  background: ${({ theme, $isSelected, $isFocused }) => {
    if ($isSelected || $isFocused) {
      return theme.color.background.tertiary;
    }
    return "transparent";
  }};
  border-radius: 4px;
  border: none;

  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.color.background.tertiary};
  }

  &:focus {
    outline: none;
    background-color: ${({ theme }) => theme.color.background.tertiary};
  }
`);

const OptionIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const SELECT_INPUT_VARIANT_TO_ICON_VARIANT_MAP: Record<SelectInputVariant, IconVariant> = {
  [SelectInputVariant.BASE]: IconVariant.SECONDARY,
  [SelectInputVariant.PRIMARY]: IconVariant.SECONDARY,
  [SelectInputVariant.SECONDARY]: IconVariant.SECONDARY,
  [SelectInputVariant.TERTIARY]: IconVariant.PRIMARY,
};

export const SELECT_INPUT_SIZE_TO_ICON_SIZE: Record<SelectInputSize, number> = {
  [SelectInputSize.SMALL]: 12,
  [SelectInputSize.MEDIUM]: 14,
  [SelectInputSize.LARGE]: 16,
};

export const SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP: Record<SelectInputSize, TextSize> = {
  [SelectInputSize.SMALL]: TextSize.BODY_MD,
  [SelectInputSize.MEDIUM]: TextSize.BODY_MD,
  [SelectInputSize.LARGE]: TextSize.BODY_MD,
};

const SelectInput = ({
  label,
  variant = SelectInputVariant.PRIMARY,
  size = SelectInputSize.MEDIUM,
  options,
  value,
  placeholder = "Select an option...",
  onChange,
  width,
  fillWidth,
  dropdownWidth,
  isDisabled = false,
  isRequired = false,
  isOpen: controlledIsOpen,
  onOpenChange,
  onReset,
  pinnedOptions = [],
  error,
  onSearch,
  debounceMs = 300,
}: SelectInputProps) => {
  const isSearchable = !!onSearch;
  const [isHovered, setIsHovered] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const optionsRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const { isOpen, setOpen } = useControlledOpenState({
    isOpen: controlledIsOpen,
    onOpenChange,
  });
  const triggerWidth = useElementWidth(triggerRef);
  const resolvedDropdownWidth = useMemo(
    () => dropdownWidth ?? triggerWidth ?? width,
    [dropdownWidth, triggerWidth, width],
  );
  const selectedOption = value ?? null;

  // Disable the select if explicitly disabled or if there's only one option
  const effectiveIsDisabled = isDisabled || options.length <= 1;

  const visible = isOpen;
  const debouncedSearchTerm = useDebouncedValue(searchTerm, debounceMs, isSearchable);

  // Auto-focus input when dropdown opens (if searchable)
  useEffect(() => {
    if (visible && isSearchable && inputRef.current) {
      inputRef.current.focus();
      // Select all text when opening for easy replacement
      inputRef.current.select();
    }
  }, [visible, isSearchable]);

  // Clear search when dropdown closes (but keep selected value)
  useEffect(() => {
    if (!visible && isSearchable) {
      setSearchTerm("");
      setFocusedOptionIndex(-1);
    }
  }, [visible, isSearchable]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!isSearchable || !debouncedSearchTerm.trim()) {
      return options;
    }
    return onSearch(debouncedSearchTerm, options);
  }, [isSearchable, onSearch, debouncedSearchTerm, options]);

  // Hide pinned options when search is active
  const filteredPinnedOptions = useMemo(() => {
    if (!isSearchable || !debouncedSearchTerm.trim()) {
      return pinnedOptions;
    }
    // Hide pinned options when search is active
    return [];
  }, [isSearchable, debouncedSearchTerm, pinnedOptions]);

  const totalOptionsCount = filteredPinnedOptions.length + filteredOptions.length;

  useEffect(() => {
    if (focusedOptionIndex >= totalOptionsCount) {
      setFocusedOptionIndex(-1);
    }
  }, [focusedOptionIndex, totalOptionsCount]);

  useEffect(() => {
    optionsRefs.current = [];
  }, [filteredPinnedOptions, filteredOptions]);

  const handleToggle = useCallback(() => {
    if (effectiveIsDisabled) return;
    setOpen(!visible);
  }, [effectiveIsDisabled, setOpen, visible]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleOptionSelect = (option: SelectInputOption) => {
    if (onChange) {
      onChange(option);
    }
    handleClose();
  };

  const handlePinnedOptionSelect = (pinnedOption: PinnedOption) => {
    const option = options.find((opt) => opt.id === pinnedOption.optionId);
    if (option && onChange) {
      onChange(option);
    }
    handleClose();
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
    handleClose();
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (effectiveIsDisabled) return;

    // If searchable and open, handle keyboard navigation in the input
    if (isSearchable && visible) {
      handleSearchKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
      return;
    }

    // Otherwise, toggle on Enter/Space
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle();
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isSearchable) return;

    const allOptions = [...filteredPinnedOptions, ...filteredOptions];
    const currentIndex = focusedOptionIndex;
    const focusOptionAtIndex = (nextIndex: number) => {
      setFocusedOptionIndex(nextIndex);
      optionsRefs.current[nextIndex]?.scrollIntoView({ block: "nearest" });
    };

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < allOptions.length - 1) {
          focusOptionAtIndex(currentIndex + 1);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          focusOptionAtIndex(currentIndex - 1);
        } else {
          // If at top, focus back to input
          inputRef.current?.focus();
        }
        break;
      case "Enter":
        e.preventDefault();
        if (currentIndex >= 0 && currentIndex < allOptions.length) {
          if (currentIndex < filteredPinnedOptions.length) {
            // Select pinned option
            handlePinnedOptionSelect(filteredPinnedOptions[currentIndex]);
          } else {
            // Select regular option
            const optionIndex = currentIndex - filteredPinnedOptions.length;
            handleOptionSelect(filteredOptions[optionIndex]);
          }
        } else if (filteredOptions.length === 1) {
          // If only one option, select it
          handleOptionSelect(filteredOptions[0]);
        }
        break;
      case "Escape":
        e.preventDefault();
        handleClose();
        break;
      default:
        // Reset focused index when typing (but allow normal input)
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
          setFocusedOptionIndex(-1);
        }
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setFocusedOptionIndex(-1);
  };

  const isPinnedOptionSelected = (pinnedOption: PinnedOption) => {
    return value?.id === pinnedOption.optionId;
  };

  const isPinnedOptionFocused = (index: number) => {
    return isSearchable && focusedOptionIndex === index && index < filteredPinnedOptions.length;
  };

  const isOptionFocused = (index: number) => {
    return (
      isSearchable && focusedOptionIndex === index + filteredPinnedOptions.length && index >= 0
    );
  };

  const dropdownBody = (
    <SelectInputContent $fillWidth={fillWidth} $width={resolvedDropdownWidth}>
      {filteredPinnedOptions.length > 0 && (
        <>
          {filteredPinnedOptions.map((pinnedOption, index) => (
            <PinnedOptionButton
              key={pinnedOption.id}
              ref={(el) => {
                optionsRefs.current[index] = el;
              }}
              $isSelected={isPinnedOptionSelected(pinnedOption)}
              $isFocused={isPinnedOptionFocused(index)}
              onClick={(e) => {
                e.preventDefault();
                handlePinnedOptionSelect(pinnedOption);
              }}
              onMouseEnter={() => setFocusedOptionIndex(index)}
            >
              <Text
                size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}
                cursor={!effectiveIsDisabled ? "pointer" : "not-allowed"}
                isEllipsis
              >
                {pinnedOption.label}
              </Text>
              {isPinnedOptionSelected(pinnedOption) && (
                <Icon
                  component={CheckIcon}
                  variant={IconVariant.TERTIARY}
                  weight={IconWeight.BOLD}
                  size={12}
                />
              )}
            </PinnedOptionButton>
          ))}
          <HorizontalDivider />
        </>
      )}
      <SelectInputOptionsContainer $fillWidth={fillWidth} $width={resolvedDropdownWidth}>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => {
            const absoluteIndex = filteredPinnedOptions.length + index;
            return (
              <SelectInputOption
                key={option.id}
                ref={(el) => {
                  optionsRefs.current[absoluteIndex] = el;
                }}
                $isSelected={option.id === value?.id}
                $isFocused={isOptionFocused(index)}
                onClick={() => handleOptionSelect(option)}
                onMouseEnter={() => setFocusedOptionIndex(absoluteIndex)}
              >
                <FlexWrapper alignItems={AlignItems.CENTER} gap={8} fillWidth>
                  {option.icon && <OptionIconSlot>{option.icon}</OptionIconSlot>}
                  <FlexItem grow={1} shrink={1} minWidth={0}>
                    <Text
                      size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}
                      cursor={!effectiveIsDisabled ? "pointer" : "not-allowed"}
                      isEllipsis
                      variant={option.variant ?? TextVariant.PRIMARY}
                    >
                      {option.label}
                    </Text>
                  </FlexItem>
                </FlexWrapper>
                {option.id === value?.id && (
                  <Icon
                    component={CheckIcon}
                    variant={IconVariant.TERTIARY}
                    weight={IconWeight.BOLD}
                    size={12}
                  />
                )}
              </SelectInputOption>
            );
          })
        ) : (
          <div style={{ padding: "8px" }}>
            <Text variant={TextVariant.TERTIARY} size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}>
              No options found
            </Text>
          </div>
        )}
      </SelectInputOptionsContainer>
      {onReset && (
        <>
          <HorizontalDivider />
          <PinnedOptionButton onClick={handleReset} $isSelected={false} $isFocused={false}>
            <FlexWrapper alignItems={AlignItems.CENTER} gap={8}>
              <Icon
                component={ArrowCounterClockwiseIcon}
                variant={IconVariant.TERTIARY}
                size={12}
              />
              <Text
                variant={TextVariant.SECONDARY}
                size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}
                cursor={!effectiveIsDisabled ? "pointer" : "not-allowed"}
              >
                Reset
              </Text>
            </FlexWrapper>
          </PinnedOptionButton>
        </>
      )}
    </SelectInputContent>
  );

  const textVariant = useMemo(() => {
    if (selectedOption) {
      return TextVariant.PRIMARY;
    }

    if (!effectiveIsDisabled && (visible || isHovered)) {
      return TextVariant.SECONDARY;
    }

    return TextVariant.TERTIARY;
  }, [selectedOption, visible, isHovered, effectiveIsDisabled]);

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth={fillWidth}>
      {label && <InputLabel label={label} isRequired={isRequired} />}
      <Dropdown
        variant={DropdownVariant.PRIMARY}
        body={dropdownBody}
        isOpen={visible}
        onClose={handleClose}
        minWidth={resolvedDropdownWidth ?? 200}
        offset={[0, 3]}
        fillWidth={fillWidth}
        noPadding
      >
        {isSearchable && visible ? (
          <StyledSelectInput
            ref={triggerRef}
            $variant={variant}
            $size={size}
            $isActive={visible ?? false}
            $isDisabled={effectiveIsDisabled}
            $isError={!!error}
            $width={width}
            $fillWidth={fillWidth}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleToggle}
          >
            <StyledSearchInput
              ref={inputRef}
              $variant={variant}
              $size={size}
              $isActive={visible ?? false}
              $isDisabled={effectiveIsDisabled}
              $isError={!!error}
              $width={width}
              $fillWidth={fillWidth}
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
              autoCorrect="off"
              disabled={effectiveIsDisabled}
              onClick={(e) => e.stopPropagation()}
            />
            <Spacing left={8} />
            <FlexWrapper>
              <RotateWithTransition isRotated={visible} deg={-180}>
                <Icon
                  component={CaretDownIcon}
                  size={10}
                  variant={
                    !effectiveIsDisabled && (isHovered || visible)
                      ? IconVariant.SECONDARY
                      : IconVariant.TERTIARY
                  }
                />
              </RotateWithTransition>
            </FlexWrapper>
          </StyledSelectInput>
        ) : (
          <StyledSelectInput
            ref={triggerRef}
            $variant={variant}
            $size={size}
            $isActive={visible ?? false}
            $isDisabled={effectiveIsDisabled}
            $isError={!!error}
            $width={width}
            $fillWidth={fillWidth}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="button"
            tabIndex={effectiveIsDisabled ? -1 : 0}
            aria-haspopup="listbox"
            aria-expanded={visible}
            aria-label={selectedOption ? selectedOption.label : placeholder}
          >
            <FlexWrapper alignItems={AlignItems.CENTER} gap={8} fillWidth>
              {selectedOption?.icon && <OptionIconSlot>{selectedOption.icon}</OptionIconSlot>}
              <FlexItem grow={1} shrink={1} minWidth={0}>
                <Text
                  variant={textVariant}
                  size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}
                  cursor={!effectiveIsDisabled ? "pointer" : "not-allowed"}
                  isEllipsis
                >
                  {selectedOption ? selectedOption.label : placeholder}
                </Text>
              </FlexItem>
            </FlexWrapper>
            <Spacing left={8} />
            <FlexWrapper>
              <RotateWithTransition isRotated={visible} deg={-180}>
                <Icon
                  component={CaretDownIcon}
                  size={10}
                  variant={
                    !effectiveIsDisabled && (isHovered || visible)
                      ? IconVariant.SECONDARY
                      : IconVariant.TERTIARY
                  }
                />
              </RotateWithTransition>
            </FlexWrapper>
          </StyledSelectInput>
        )}
      </Dropdown>
      {error && (
        <Text variant={TextVariant.ERROR} size={TextSize.BODY_SM}>
          {error}
        </Text>
      )}
    </FlexWrapper>
  );
};

export default SelectInput;
